<template>
    <div>
        <grid-layout
            v-if="currentLayout && currentLayout.length"
            :layout.sync="currentLayout"
            :colNum="24"
            :rowHeight="10"
            :verticalCompact="false"
            :useCssTransforms="false"
            :isDraggable="false"
            :isResizable="false"
            :responsive="false"
            :margin="[28, 28]"
        >
            <grid-item v-for="(item, index) in currentLayout"
                class="card"
                :preserveAspectRatio="true"
                :key="index"
                :static="item.static"
                :x="item.x"
                :y="item.y"
                :w="item.w"
                :h="item.h"
                :i="item.i"
                :isBounded="true"
            >
                <component
                    :is="item.i"
                    :editMode="false"
                    :isOpen="isOpenStatusByCard(item.i)"
                    @modal:showDetail="showDetailModal"
                    @action:showTable="showTableBelow"
                />
            </grid-item>
        </grid-layout>
        <b-modal
            modalClass="modal-card-detail"
            ref="MODAL_CARD_DETAIL"
            ok-only
            ok-title="Close"
            size="lg"
            centered
            :title="detailLayerTitle"
        >
            <div v-for="(item, itemIdx) in detail" :key="itemIdx">
                <h5 class="p-25" :class="{ 'mt-1': itemIdx > 0 }">
                    {{ item.key }}
                </h5>
                <p class="mb-0 p-25"
                    v-for="(detailItem, detailItemIdx) in item.detail" :key="detailItemIdx"
                    :style="{'border-top': (detailItemIdx !== 0 && detailItemIdx % 5 === 0 ) ? '1px solid ' + themeColors.secondary : ''}"
                    >
                    <b class="float-right">
                        {{ $_getDollar(detailItem.cost) }}
                    </b>
                    {{ detailItem.itemDescription }}
                    <span
                        v-if="isNaN(detailItem.unblendedRate)"
                    >
                        (no rate)
                    </span>
                    <span v-else>
                        ({{ detailItem.unblendedRate }})
                    </span>
                    <small class="text-nowrap">
                        x {{ $_getNumberFormat(detailItem.usageAmount) }}
                    </small>
                </p>
            </div>
        </b-modal>
    </div>
</template>

<script>
import bus from '@/libs/bus'
import { ref, getCurrentInstance, computed, watch, inject, onMounted } from '@vue/composition-api'
import { GridLayout, GridItem } from 'vue-grid-layout'
import useSelectProjectAndGroup from '@/composables/useSelectProjectAndGroup'
import useSelectMonth from '@/composables/useSelectMonth'
import useDashboard from '@/composables/useDashboard'
import useDashboardRender from '@/composables/useDashboardRender'
import cardType from '../cardType'
import useCardCollapse from '@/composables/useCardCollapse'
import useTour from '@/composables/useTour'
const CardTotalCost = () => import('./CardTotalCost.vue')
const CardDownload = () => import('./CardDownload.vue')
const CardMonths = () => import('./CardMonths.vue')
const CardWeeks = () => import('./CardWeeks.vue')
const CardDates = () => import('./CardDates.vue')
const CardProducts = () => import('./CardProducts.vue')
const CardRegions = () => import('./CardRegions.vue')
const CardStatistics = () => import('./CardStatistics.vue')
const CardBanner = () => import('./CardBanner.vue')
const CardBoard = () => import('./CardBoard.vue')
const CardDatesDetail = () => import('./CardDatesDetail.vue')
const SummaryCardSidebar = () => import('./summary/SideBar.vue')
const SummaryCardProducts = () => import('./summary/ProductCard.vue')
const SummaryCardRegions = () => import('./summary/RegionCard.vue')
const SummaryCardDates = () => import('./summary/DailyLine.vue')
const SummaryCardWeeks = () => import('./summary/WeeklyHeatmap.vue')

export default {
    components: {
        GridLayout,
        GridItem,
        CardTotalCost,
        CardDownload,
        CardMonths,
        CardWeeks,
        CardDates,
        CardProducts,
        CardRegions,
        CardStatistics,
        CardBanner,
        CardBoard,
        CardDatesDetail,
        SummaryCardSidebar,
        SummaryCardProducts,
        SummaryCardRegions,
        SummaryCardDates,
        SummaryCardWeeks
    },
    setup () {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const themeColors = inject('themeColors')
        const { qtypeParam, changeProject } = useSelectProjectAndGroup()
        const { selectedMonth } = useSelectMonth()
        const { getSummaryDetail } = useDashboard()
        const {
            layout,
            viewType,
            windowWidth,
            sortCardsByPosition,
            getCurrentLayoutMobile,
            layoutReducer
        } = useDashboardRender()
        const initialLayout = ref(layout.value)
        const currentLayout = ref(JSON.parse(JSON.stringify(initialLayout.value)))
        const sortedCards = sortCardsByPosition(initialLayout.value)
        const viewTypeIsMobile = computed(() => viewType.value === 'mobile')
        const { isDoneTourDashboard, cancelTour } = useTour()
        const { xsLayout, smLayout } = getCurrentLayoutMobile(sortedCards)
        const mobileBaseLayoutMap = smLayout.reduce(layoutReducer, {})
        const mobileLayoutMap = {
            xs: xsLayout.reduce(layoutReducer, {}),
            sm: mobileBaseLayoutMap,
            md: mobileBaseLayoutMap
        }
        const openCardListArr = [
            cardType.CARD_WEEKS,
            cardType.CARD_DATES,
            cardType.CARD_PRODUCTS,
            cardType.CARD_DATES_DETAIL,
            cardType.SUMMARY_CARD_PRODUCTS,
            cardType.SUMMARY_CARD_REGIONS,
            cardType.SUMMARY_CARD_DATES,
            cardType.SUMMARY_CARD_WEEKS
        ]
        const openCardList = initialLayout.value.filter(item => openCardListArr.includes(item.i)).map(item => item.i)

        const openedStatus = ref(0)
        const { getEveryLayoutsOfOpenedStatus } = useCardCollapse()
        const layoutsOfOpenedCard = getEveryLayoutsOfOpenedStatus(initialLayout.value, openCardList)
        const [detailStatus, detail] = getSummaryDetail()
        const detailLayerTitle = ref(null)
        const MODAL_CARD_DETAIL = ref(null)
        const showDetailModal = (param) => {
            detailLayerTitle.value = [param.productCode, param.region, param.date].filter(item => item !== undefined && item !== '').join(', ')
            getSummaryDetail({ ...qtypeParam.value, month: selectedMonth.value, ...param })[2].then(() => {
                if (MODAL_CARD_DETAIL.value) MODAL_CARD_DETAIL.value.show()
            })
        }
        const getOpenStatusByCard = (component) => {
            const componentIndex = openCardList.indexOf(component)
            if (componentIndex < 0) return 0
            const componentValue = 1 << componentIndex
            return componentValue
        }
        const isOpenStatusByCard = (component) => {
            if (openedStatus.value === 0) return false
            const componentValue = getOpenStatusByCard(component)
            if (!componentValue) return false
            return !!(componentValue & openedStatus.value)
        }
        const showTableBelow = ({ component, status }) => {
            const componentValue = getOpenStatusByCard(component)
            openedStatus.value = status
                ? openedStatus.value | componentValue
                : openedStatus.value ^ componentValue
            currentLayout.value = layoutsOfOpenedCard[openedStatus.value.toString(2).padStart(openCardList.length, '0')]
        }
        const getCurrentLayoutByViewType = () => {
            currentLayout.value = viewTypeIsMobile.value
                ? currentLayout.value.map(item => mobileLayoutMap[windowWidth.value <= 576 ? 'xs' : 'sm'][item.i])
                : layoutsOfOpenedCard[openedStatus.value.toString(2).padStart(openCardList.length, '0')]
        }

        if (viewTypeIsMobile.value) {
            currentLayout.value = currentLayout.value.map(item => mobileLayoutMap[windowWidth.value <= 576 ? 'xs' : 'sm'][item.i])
        }
        watch(windowWidth, (value, oldValue) => {
            getCurrentLayoutByViewType()
            if (viewTypeIsMobile.value) {
                cancelTour()
                openedStatus.value = 0
            }
        })
        watch(initialLayout, (value, oldValue) => {
            currentLayout.value = JSON.parse(JSON.stringify(value))
        })
        const setCurrentParams = () => {
            store.commit('$_dashboard/FETCH_CURRENT_PARAMS', {
                ...qtypeParam.value,
                month: selectedMonth.value
            })
        }
        setCurrentParams()
        watch(changeProject, (value, oldValue) => {
            setCurrentParams()
            openedStatus.value = 0
            getCurrentLayoutByViewType()
        })
        watch(selectedMonth, (value, oldValue) => {
            setCurrentParams()
            openedStatus.value = 0
            getCurrentLayoutByViewType()
        })
        onMounted(() => {
            if (isDoneTourDashboard.value || viewTypeIsMobile.value) return
            bus.$emit('tour-start:dashboard')
        })
        return {
            themeColors,
            openedStatus,
            isOpenStatusByCard,
            getOpenStatusByCard,
            showDetailModal,
            showTableBelow,
            MODAL_CARD_DETAIL,
            detail,
            detailLayerTitle,
            detailStatus,
            currentLayout
        }
    }
}
</script>
<style>

</style>
